/**
 * navigation/authChannel.ts
 * Same-origin cross-tab signal for the auth flow. When the user clicks the
 * verification link (which opens the Verify screen, usually in a NEW tab), we
 * broadcast "email verified" so the original "Check your email" tab can advance
 * itself to the login screen instead of sitting there forever.
 *
 * Web-only (BroadcastChannel + localStorage). No-op on native.
 */
const CHANNEL = 'sk-auth';
const STORAGE_KEY = 'sk_email_verified_at';
const RESET_STORAGE_KEY = 'sk_reset_opened_at';

interface VerifiedPayload {
  type: 'email-verified';
  email?: string;
  at: number;
}

interface ResetOpenedPayload {
  type: 'reset-opened';
  token: string;
  at: number;
}

/** Fire after a successful email verification. */
export function notifyEmailVerified(email?: string): void {
  if (typeof window === 'undefined') return;
  const payload: VerifiedPayload = { type: 'email-verified', email, at: Date.now() };
  try {
    if ('BroadcastChannel' in window) {
      const bc = new BroadcastChannel(CHANNEL);
      bc.postMessage(payload);
      bc.close();
    }
  } catch {
    /* ignore — fall back to storage below */
  }
  // `storage` events fire in OTHER tabs only — exactly the check-email tab.
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

/** Subscribe to "email verified" from any other tab. Returns an unsubscribe fn. */
export function onEmailVerified(cb: (email?: string) => void): () => void {
  if (typeof window === 'undefined') return () => {};

  let bc: BroadcastChannel | null = null;
  try {
    if ('BroadcastChannel' in window) {
      bc = new BroadcastChannel(CHANNEL);
      bc.onmessage = (e: MessageEvent<VerifiedPayload>) => {
        if (e.data?.type === 'email-verified') cb(e.data.email);
      };
    }
  } catch {
    bc = null;
  }

  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY && e.newValue) {
      try {
        const p = JSON.parse(e.newValue) as VerifiedPayload;
        if (p.type === 'email-verified') cb(p.email);
      } catch {
        /* ignore malformed */
      }
    }
  };
  window.addEventListener('storage', onStorage);

  return () => {
    if (bc) bc.close();
    window.removeEventListener('storage', onStorage);
  };
}

/**
 * Fire when the reset-password screen opens from an emailed link (it holds the
 * token). The original "reset link sent" tab listens and advances itself to the
 * reset-password screen — same cross-tab pattern as email verification. The
 * token is same-origin only (already present in the reset tab's URL), so this
 * exposes nothing a same-origin script couldn't already read.
 */
export function notifyResetOpened(token: string): void {
  if (typeof window === 'undefined' || !token) return;
  const payload: ResetOpenedPayload = { type: 'reset-opened', token, at: Date.now() };
  try {
    if ('BroadcastChannel' in window) {
      const bc = new BroadcastChannel(CHANNEL);
      bc.postMessage(payload);
      bc.close();
    }
  } catch {
    /* ignore — fall back to storage below */
  }
  try {
    localStorage.setItem(RESET_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

/** Subscribe to "reset link opened" from any other tab. Returns an unsubscribe fn. */
export function onResetOpened(cb: (token: string) => void): () => void {
  if (typeof window === 'undefined') return () => {};

  let bc: BroadcastChannel | null = null;
  try {
    if ('BroadcastChannel' in window) {
      bc = new BroadcastChannel(CHANNEL);
      bc.onmessage = (e: MessageEvent<ResetOpenedPayload>) => {
        if (e.data?.type === 'reset-opened' && e.data.token) cb(e.data.token);
      };
    }
  } catch {
    bc = null;
  }

  const onStorage = (e: StorageEvent) => {
    if (e.key === RESET_STORAGE_KEY && e.newValue) {
      try {
        const p = JSON.parse(e.newValue) as ResetOpenedPayload;
        if (p.type === 'reset-opened' && p.token) cb(p.token);
      } catch {
        /* ignore malformed */
      }
    }
  };
  window.addEventListener('storage', onStorage);

  return () => {
    if (bc) bc.close();
    window.removeEventListener('storage', onStorage);
  };
}
